import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { formatAmount } from '../../utils/formatters';
import { parseDate, toDateString, formatDate } from '../../utils/dateUtils';
import BillIcon from '../common/BillIcon';
import OwnershipBadge from '../common/OwnershipBadge';
import {

  Calendar as CalendarIcon,
  Check,
  Clock,
  AlertCircle,
  Zap,
  Plus,
  DollarSign
} from 'lucide-react';
import '../../styles/Calendar.css';

// Constants
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Helper functions
const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

const isToday = (date) => {
  const t = new Date();
  return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
};

/**
 * CalendarView - A reusable calendar grid component for displaying bills
 * 
 * Props:
 * - bills: Array of bill objects
 * - payments: Array of payment objects  
 * - categories: Array of category objects
 * - onBillClick: (bill, date) => void - Called when a bill is clicked
 * - onAddClick: (date) => void - Called when add button is clicked
 * - onPaydayClick: (payday) => void - Called when a payday is clicked
 * - currentDate: Date - The current month being viewed
 * - onDateChange: (date) => void - Called when month changes
 */
function CalendarView({ 
  bills = [], 
  payments = [], 
  categories = [],
  onBillClick,
  onAddClick,
  onPaydayClick,
  currentDate,
  onDateChange,
  incomeCalcs = null
}) {
  // Calendar state
  const [selectedDay, setSelectedDay] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [compactMode, setCompactMode] = useState(false);
  const [maxBillsPerDay, setMaxBillsPerDay] = useState(3);

  // Refs
  const gridRef = useRef(null);

  // ===========================================
  // HELPERS
  // ===========================================

  const isBillPaid = useCallback((billId, date) => {
    const target = parseDate(toDateString(date))?.getTime();
    if (!target) return false;
    const early = 14 * 24 * 60 * 60 * 1000;
    const late = 7 * 24 * 60 * 60 * 1000;
    return payments.some(p => {
      if (p.bill_id !== billId) return false;
      const pt = parseDate(p.payment_date)?.getTime();
      return pt && pt >= target - early && pt <= target + late;
    });
  }, [payments]);

  const getCategoryInfo = useCallback((name) => {
    const c = categories.find(x => x.name === name);
    return c ? { icon: c.icon } : { icon: '📋' };
  }, [categories]);

  // ===========================================
  // BILL CALCULATION
  // ===========================================

  const getBillsForDate = useMemo(() => (date) => {
    if (!date) return [];
    const t = new Date(date);
    const dow = t.getDay(), dom = t.getDate(), m = t.getMonth(), y = t.getFullYear();

    return bills.filter(bill => {
      if (!bill.is_active) return false;
      
      const start = parseDate(bill.start_date);
      if (!start || t < start) return false;
      if (bill.end_date) {
        const end = parseDate(bill.end_date);
        if (end && t > end) return false;
      }

      const freq = bill.billing_frequency;
      const due = bill.due_day;

      switch (freq) {
        case 'weekly': return dow === due;
        case 'biweekly': {
          const sw = Math.floor(start.getTime() / 604800000);
          const tw = Math.floor(t.getTime() / 604800000);
          return dow === due && (tw - sw) % 2 === 0;
        }
        case 'monthly': {
          const dim = getDaysInMonth(y, m);
          return dom === (due > dim ? dim : due);
        }
        case 'quarterly': {
          const md = (y - start.getFullYear()) * 12 + (m - start.getMonth());
          if (md < 0 || md % 3 !== 0) return false;
          const dim = getDaysInMonth(y, m);
          return dom === (due > dim ? dim : due);
        }
        case 'annually': {
          if (m !== start.getMonth()) return false;
          const dim = getDaysInMonth(y, m);
          const sd = start.getDate();
          return dom === (sd > dim ? dim : sd);
        }
        case 'one-time':
          return t.getFullYear() === start.getFullYear() && t.getMonth() === start.getMonth() && t.getDate() === start.getDate();
        default: {
          const match = freq?.match(/^(\d+)-(weekly|monthly)$/);
          if (match) {
            const int = parseInt(match[1]);
            if (match[2] === 'weekly') {
              const sw = Math.floor(start.getTime() / 604800000);
              const tw = Math.floor(t.getTime() / 604800000);
              return dow === due && (tw - sw) % int === 0;
            } else {
              const md = (y - start.getFullYear()) * 12 + (m - start.getMonth());
              return md >= 0 && md % int === 0 && dom === due;
            }
          }
          return false;
        }
      }
    });
  }, [bills]);

  // ===========================================
  // CALENDAR DATA
  // ===========================================

  const calendarData = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const dim = getDaysInMonth(y, m);
    const first = getFirstDayOfMonth(y, m);
    const prevDim = getDaysInMonth(y, m - 1);

    // Get paydays for the visible range
    const startOfView = new Date(y, m - 1, prevDim - first + 1);
    const endOfView = new Date(y, m + 1, 14); // Enough to cover next month overflow
    const paydays = incomeCalcs?.getPaydaysInRange ? incomeCalcs.getPaydaysInRange(startOfView, endOfView) : [];
    
    // Create a map of paydays by date string for quick lookup
    const paydayMap = {};
    paydays.forEach(p => {
      if (!paydayMap[p.date]) paydayMap[p.date] = [];
      paydayMap[p.date].push(p);
    });

    const days = [];

    // Previous month
    for (let i = first - 1; i >= 0; i--) {
      const d = prevDim - i;
      const date = new Date(y, m - 1, d);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push({ date, day: d, current: false, bills: getBillsForDate(date), paydays: paydayMap[dateStr] || [] });
    }

    // Current month
    for (let d = 1; d <= dim; d++) {
      const date = new Date(y, m, d);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push({ date, day: d, current: true, bills: getBillsForDate(date), paydays: paydayMap[dateStr] || [] });
    }

    // Next month
    const rem = days.length % 7;
    if (rem > 0) {
      for (let d = 1; d <= 7 - rem; d++) {
        const date = new Date(y, m + 1, d);
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        days.push({ date, day: d, current: false, bills: getBillsForDate(date), paydays: paydayMap[dateStr] || [] });
      }
    }

    return days;
  }, [currentDate, getBillsForDate, incomeCalcs]);

  const rows = Math.ceil(calendarData.length / 7);

  // ===========================================
  // HANDLERS
  // ===========================================



  const onDayClick = (date, e) => {
    const cell = e.currentTarget;
    const grid = gridRef.current;
    if (!cell || !grid) {
      setSelectedDay(date);
      setPopupPosition(null);
      return;
    }
    
    const cellRect = cell.getBoundingClientRect();
    const gridRect = grid.getBoundingClientRect();
    
    const popupWidth = 340;
    const popupHeight = 380;
    const gap = 4;
    
    const spaceRight = gridRect.right - cellRect.right;
    const spaceLeft = cellRect.left - gridRect.left;
    const spaceBelow = window.innerHeight - cellRect.bottom - 20;
    const spaceAbove = cellRect.top - gridRect.top;
    
    let left, top, placement;
    const isNearBottom = spaceBelow < popupHeight;
    
    if (spaceRight >= popupWidth + gap) {
      placement = 'right';
      left = cellRect.right - gridRect.left + gap;
      top = cellRect.top - gridRect.top;
      if (top + popupHeight > gridRect.height) {
        top = Math.max(0, gridRect.height - popupHeight);
      }
    } else if (spaceLeft >= popupWidth + gap) {
      placement = 'left';
      left = cellRect.left - gridRect.left - popupWidth - gap;
      top = cellRect.top - gridRect.top;
      if (top + popupHeight > gridRect.height) {
        top = Math.max(0, gridRect.height - popupHeight);
      }
    } else if (isNearBottom && spaceAbove >= popupHeight + gap) {
      placement = 'top';
      top = cellRect.top - gridRect.top - popupHeight - gap;
      left = cellRect.left - gridRect.left + (cellRect.width / 2) - (popupWidth / 2);
      if (left < 0) left = 0;
      if (left + popupWidth > gridRect.width) left = gridRect.width - popupWidth;
    } else if (!isNearBottom) {
      placement = 'bottom';
      top = cellRect.bottom - gridRect.top + gap;
      left = cellRect.left - gridRect.left + (cellRect.width / 2) - (popupWidth / 2);
      if (left < 0) left = 0;
      if (left + popupWidth > gridRect.width) left = gridRect.width - popupWidth;
    } else {
      placement = 'top';
      top = Math.max(0, cellRect.top - gridRect.top - popupHeight - gap);
      left = cellRect.left - gridRect.left + (cellRect.width / 2) - (popupWidth / 2);
      if (left < 0) left = 0;
      if (left + popupWidth > gridRect.width) left = gridRect.width - popupWidth;
    }
    
    setSelectedDay(date);
    setPopupPosition({ left, top, placement });
  };

  const handleBillClick = (bill, date, e) => { 
    e.stopPropagation(); 
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isPaid = isBillPaid(bill.id, date);
    const isOverdue = !isPaid && date < today;
    
    // Find associated payment if paid
    let payment = null;
    if (isPaid) {
      const target = parseDate(toDateString(date))?.getTime();
      if (target) {
        const early = 14 * 24 * 60 * 60 * 1000;
        const late = 7 * 24 * 60 * 60 * 1000;
        payment = payments.find(p => {
          if (p.bill_id !== bill.id) return false;
          const pt = parseDate(p.payment_date)?.getTime();
          return pt && pt >= target - early && pt <= target + late;
        });
      }
    }
    
    const billWithDueInfo = {
      ...bill,
      dueInfo: {
        dueDate: toDateString(date),
        isPaid,
        isOverdue
      }
    };
    if (onBillClick) onBillClick(billWithDueInfo, date, payment);
  };

  // ===========================================
  // CLICK OUTSIDE TO CLOSE POPUP
  // ===========================================

  useEffect(() => {
    if (!selectedDay) return;
    
    const handleClickOutside = (e) => {
      const popup = document.querySelector('.day-popup');
      if (popup && popup.contains(e.target)) return;
      const calDay = e.target.closest('.cal-day');
      if (calDay) return;
      setSelectedDay(null);
    };
    
    const timeout = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeout);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedDay]);

  // ===========================================
  // COMPACT MODE DETECTION
  // ===========================================

  useEffect(() => {
    let timeoutId;
    
    const checkCompactMode = () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        const grid = gridRef.current;
        if (!grid) return;
        
        const firstCell = grid.querySelector('.cal-day');
        if (!firstCell) return;
        
        const cellHeight = firstCell.getBoundingClientRect().height;
        const cellWidth = firstCell.getBoundingClientRect().width;
        const isCompact = cellHeight < 100 || cellWidth < 100;
        setCompactMode(isCompact);
        
        // Calculate how many bill items fit: header ~30px, each item ~28px, "+more" ~20px
        const availableHeight = cellHeight - 30;
        const itemHeight = 28;
        const moreHeight = 20;
        const fitCount = Math.max(1, Math.floor((availableHeight - moreHeight) / itemHeight));
        setMaxBillsPerDay(isCompact ? 6 : fitCount);
      }, 50);
    };
    
    const initialTimeout = setTimeout(checkCompactMode, 100);
    
    const resizeObserver = new ResizeObserver(checkCompactMode);
    if (gridRef.current) {
      resizeObserver.observe(gridRef.current);
    }
    
    window.addEventListener('resize', checkCompactMode);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkCompactMode);
    };
  }, [rows]);

  // ===========================================
  // DERIVED
  // ===========================================

  const selectedBills = selectedDay ? getBillsForDate(selectedDay) : [];
  const selectedPaydays = selectedDay ? (() => {
    const dateStr = `${selectedDay.getFullYear()}-${String(selectedDay.getMonth() + 1).padStart(2, '0')}-${String(selectedDay.getDate()).padStart(2, '0')}`;
    const dayData = calendarData.find(d => {
      const dStr = `${d.date.getFullYear()}-${String(d.date.getMonth() + 1).padStart(2, '0')}-${String(d.date.getDate()).padStart(2, '0')}`;
      return dStr === dateStr;
    });
    return dayData?.paydays || [];
  })() : [];
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const formatFull = (d) => formatDate(d, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <div className="calendar-view-wrapper calendar-page">
      {/* Calendar Grid */}
      <div className="calendar-card">
        <div className="cal-weekdays">
          {DAYS.map(d => <div key={d} className="cal-weekday">{d}</div>)}
        </div>

        <div className="cal-grid" ref={gridRef} style={{ '--rows': rows }}>
          {calendarData.map((item, i) => {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const sorted = [...item.bills].sort((a, b) => {
              const ap = isBillPaid(a.id, item.date), bp = isBillPaid(b.id, item.date);
              const ao = !ap && item.date < today, bo = !bp && item.date < today;
              if (ao !== bo) return ao ? -1 : 1;
              if (ap !== bp) return ap ? 1 : -1;
              const aLogo = !!a.icon_url, bLogo = !!b.icon_url;
              if (aLogo !== bLogo) return aLogo ? -1 : 1;
              return 0;
            });

            const show = sorted.slice(0, maxBillsPerDay);
            const more = sorted.length - maxBillsPerDay;
            const total = sorted.reduce((s, b) => s + parseFloat(b.amount), 0);
            const sel = selectedDay && item.date.getTime() === selectedDay.getTime();

            return (
              <div
                key={i}
                className={`cal-day ${!item.current ? 'other' : ''} ${isToday(item.date) ? 'today' : ''} ${sel ? 'selected' : ''}`}
                onClick={(e) => onDayClick(item.date, e)}
              >
                <div className="day-top">
                  <div className="day-top-left">
                    <span className="day-num">{item.day}</span>
                    {isToday(item.date) && <span className="today-badge">TODAY</span>}
                  </div>
                  <div className="day-top-right">
                    {/* Payday indicator */}
                    {item.paydays && item.paydays.length > 0 && (
                      <span className="payday-badge" title={item.paydays.map(p => p.income_name).join(', ')}>
                        <DollarSign size={12} />
                      </span>
                    )}
                    {sorted.length > 0 && item.current && <span className="day-total">{formatAmount(total)}</span>}
                  </div>
                </div>

                {sorted.length > 0 && (
                  <div className={`day-bills ${compactMode ? 'compact' : ''}`}>
                    {compactMode ? (
                      <>
                        {sorted.slice(0, 6).map((bill, j) => {
                          const paid = isBillPaid(bill.id, item.date);
                          const over = !paid && item.date < today;
                          const sevenDaysFromNow = new Date(today);
                          sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                          const dueSoon = !paid && !over && item.date <= sevenDaysFromNow;
                          return (
                            <span 
                              key={j} 
                              className={`bill-dot ${paid ? 'paid' : ''} ${over ? 'overdue' : ''} ${dueSoon ? 'due-soon' : ''}`}
                              style={!paid && !over && !dueSoon && bill.accent_color ? { background: bill.accent_color } : undefined}
                            />
                          );
                        })}
                        {sorted.length > 6 && <span className="dot-more">+{sorted.length - 6}</span>}
                      </>
                    ) : (
                      <>
                        {show.map((bill, j) => {
                          const paid = isBillPaid(bill.id, item.date);
                          const over = !paid && item.date < today;
                          const sevenDaysFromNow = new Date(today);
                          sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                          const dueSoon = !paid && !over && item.date <= sevenDaysFromNow;
                          return (
                            <div key={j} className={`day-bill ${paid ? 'paid' : ''} ${over ? 'overdue' : ''} ${dueSoon ? 'due-soon' : ''} ${bill.icon_url ? 'has-logo' : ''}`}
                                 style={bill.icon_url && !paid && !over && !dueSoon && bill.accent_color ? { '--bill-accent': bill.accent_color } : undefined}>
                              {bill.icon_url ? (
                                <img 
                                  src={bill.icon_url} 
                                  alt={bill.bill_name} 
                                  className="bill-logo" 
                                  title={bill.bill_name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                              ) : null}
                              <span 
                                className="bill-name" 
                                style={{
                                  ...(bill.accent_color ? { color: bill.accent_color } : {}),
                                  ...(bill.icon_url ? { display: 'none' } : {})
                                }}
                              >
                                {bill.bill_name}
                              </span>
                            </div>
                          );
                        })}
                        {more > 0 && <div className="day-more">+{more} more</div>}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop Popup */}
        {selectedDay && !isMobile && popupPosition && (
          <div 
            className="day-popup" 
            style={{ 
              left: popupPosition.left, 
              top: popupPosition.top,
              transform: 'none'
            }}
          >
            <div className="popup-head">
              <h3>{formatFull(selectedDay)}</h3>
              <div className="popup-actions">
                {onAddClick && <button className="icon-btn primary" onClick={() => onAddClick(selectedDay)}><Plus size={16} /></button>}
                <button className="icon-btn" onClick={() => setSelectedDay(null)}>×</button>
              </div>
            </div>

            {selectedBills.length === 0 && selectedPaydays.length === 0 ? (
              <div className="popup-empty"><CalendarIcon size={28} /><span>No bills due</span></div>
            ) : (
              <>
                {selectedBills.length > 0 && (
                  <div className="popup-total">
                    <span>Total Due</span>
                    <strong>{formatAmount(selectedBills.reduce((s, b) => s + parseFloat(b.amount), 0))}</strong>
                  </div>
                )}
                <div className="popup-list">
                  {/* Paydays */}
                  {selectedPaydays.map((payday, i) => (
                    <div 
                      key={`payday-${i}`} 
                      className={`popup-item payday ${onPaydayClick ? 'clickable' : ''}`}
                      onClick={() => onPaydayClick && onPaydayClick(payday)}
                      style={onPaydayClick ? { cursor: 'pointer' } : undefined}
                    >
                      <div className="popup-payday-icon">
                        {payday.icon_url ? (
                          <img src={payday.icon_url} alt="" className="payday-logo" />
                        ) : (
                          <DollarSign size={20} />
                        )}
                      </div>
                      <div className="popup-info">
                        <div className="popup-name payday">{payday.income_name}</div>
                        <div className="popup-meta"><span>Payday</span></div>
                      </div>
                      <div className="popup-right">
                        <span className="popup-amt payday">+{formatAmount(payday.amount)}</span>
                      </div>
                    </div>
                  ))}
                  {/* Bills */}
                  {selectedBills.map((bill, i) => {
                    const cat = getCategoryInfo(bill.category);
                    const paid = isBillPaid(bill.id, selectedDay);
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const over = !paid && selectedDay < today;
                    const sevenDaysFromNow = new Date(today);
                    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                    const dueSoon = !paid && !over && selectedDay <= sevenDaysFromNow;
                    return (
                      <div key={i} className={`popup-item ${paid ? 'paid' : ''} ${over ? 'overdue' : ''} ${dueSoon ? 'due-soon' : ''}`} onClick={(e) => handleBillClick(bill, selectedDay, e)}>
                        <BillIcon iconUrl={bill.icon_url} categoryIcon={cat.icon} billName={bill.bill_name} size="md" />
                        <div className="popup-info">
                          <div className="popup-name">{bill.bill_name}{bill.auto_pay && <Zap size={12} />}</div>
                          <div className="popup-meta"><span>{bill.category}</span><OwnershipBadge isHousehold={!!bill.household_id} variant="icon-only" size="sm" /></div>
                        </div>
                        <div className="popup-right">
                          <span className="popup-amt">{formatAmount(bill.amount)}</span>
                          {paid && <span className="badge paid"><Check size={10} /> Paid</span>}
                          {over && <span className="badge overdue"><AlertCircle size={10} /> Overdue</span>}
                          {dueSoon && <span className="badge due-soon"><Clock size={10} /> Due Soon</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Mobile Sheet */}
      {selectedDay && isMobile && (
        <>
          <div className="sheet-overlay" onClick={() => setSelectedDay(null)} />
          <div className="sheet">
            <div className="popup-head">
              <h3>{formatFull(selectedDay)}</h3>
              <div className="popup-actions">
                {onAddClick && <button className="icon-btn primary" onClick={() => onAddClick(selectedDay)}><Plus size={16} /></button>}
                <button className="icon-btn" onClick={() => setSelectedDay(null)}>×</button>
              </div>
            </div>

            {selectedBills.length === 0 && selectedPaydays.length === 0 ? (
              <div className="popup-empty"><CalendarIcon size={28} /><span>No bills due</span></div>
            ) : (
              <>
                {selectedBills.length > 0 && (
                  <div className="popup-total">
                    <span>Total Due</span>
                    <strong>{formatAmount(selectedBills.reduce((s, b) => s + parseFloat(b.amount), 0))}</strong>
                  </div>
                )}
                <div className="popup-list">
                  {/* Paydays */}
                  {selectedPaydays.map((payday, i) => (
                    <div 
                      key={`payday-${i}`} 
                      className={`popup-item payday ${onPaydayClick ? 'clickable' : ''}`}
                      onClick={() => onPaydayClick && onPaydayClick(payday)}
                      style={onPaydayClick ? { cursor: 'pointer' } : undefined}
                    >
                      <div className="popup-payday-icon">
                        {payday.icon_url ? (
                          <img src={payday.icon_url} alt="" className="payday-logo" />
                        ) : (
                          <DollarSign size={20} />
                        )}
                      </div>
                      <div className="popup-info">
                        <div className="popup-name payday">{payday.income_name}</div>
                        <div className="popup-meta"><span>Payday</span></div>
                      </div>
                      <div className="popup-right">
                        <span className="popup-amt payday">+{formatAmount(payday.amount)}</span>
                      </div>
                    </div>
                  ))}
                  {/* Bills */}
                  {selectedBills.map((bill, i) => {
                    const cat = getCategoryInfo(bill.category);
                    const paid = isBillPaid(bill.id, selectedDay);
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const over = !paid && selectedDay < today;
                    const sevenDaysFromNow = new Date(today);
                    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                    const dueSoon = !paid && !over && selectedDay <= sevenDaysFromNow;
                    return (
                      <div key={i} className={`popup-item ${paid ? 'paid' : ''} ${over ? 'overdue' : ''} ${dueSoon ? 'due-soon' : ''}`} onClick={(e) => handleBillClick(bill, selectedDay, e)}>
                        <BillIcon iconUrl={bill.icon_url} categoryIcon={cat.icon} billName={bill.bill_name} size="md" />
                        <div className="popup-info">
                          <div className="popup-name">{bill.bill_name}{bill.auto_pay && <Zap size={12} />}</div>
                          <div className="popup-meta"><span>{bill.category}</span></div>
                        </div>
                        <div className="popup-right">
                          <span className="popup-amt">{formatAmount(bill.amount)}</span>
                          {paid && <span className="badge paid"><Check size={10} /> Paid</span>}
                          {over && <span className="badge overdue"><AlertCircle size={10} /> Overdue</span>}
                          {dueSoon && <span className="badge due-soon"><Clock size={10} /> Due Soon</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CalendarView;
